/** Decimal hour from Postgres `time` / "HH:MM" / "HH:MM:SS". */
export const PX_PER_HOUR = 60;

export function parseTimeToHour(timeStr) {
  if (!timeStr) return null;
  const parts = String(timeStr).split(':').map(Number);
  const h = parts[0];
  const m = parts[1] || 0;
  if (!Number.isFinite(h)) return null;
  return h + m / 60;
}

/** True when at least one row for the day has both set times (Schedule tab gate). */
export function lineupHasScheduleData(lineup = [], day = null) {
  const rows = day ? lineup.filter((row) => row.performance_day === day) : lineup;
  return rows.some((row) => row.set_start_time && row.set_end_time);
}

/** Rows with a start time for schedule rendering. A set with no end (a headliner the poster prints only a start for)
 *  is drawn for OPEN_SET_HOURS: dropping it lost both of Head Trip's headliners from the grid (2026-10-07). */
export function scheduleRowsForDay(lineup = [], day) {
  return lineup.filter((row) => (!day || row.performance_day === day) && row.set_start_time);
}
/** How long a set with no listed end is drawn: an hour, the same slot the map's stage cards assume. */
export const OPEN_SET_HOURS = 1;

/**
 * Set times are stored as time-of-day, so a night comes back scrambled: 23:00-01:00 has end < start,
 * and a 02:00 set on that night reads as the earliest set of the day. Running all night is normal
 * (operator, 2026-09-25: some festivals go 10 PM to 5 or 6 AM), so no clock hour is special. Instead
 * each day starts where its lineup does: after the longest stretch of the clock with no set starting.
 * A daytime festival (12:00-23:00) starts at noon; a night one (22:00-06:00) starts at 22:00 and its
 * small hours count on from 24.
 */
/** Longer than this after wrapping is bad data, not a long set (14:00-13:00 is not a 23-hour set). */
const MAX_SET_HOURS = 12;

/** The clock hour a day's lineup begins at: the first start after its longest idle gap. Null if untimed. */
export function dayStartHour(rows = []) {
  const starts = [
    ...new Set(
      rows
        .map((r) => parseTimeToHour(r?.set_start_time))
        .filter((h) => h != null)
        .map((h) => ((h % 24) + 24) % 24),
    ),
  ].sort((a, b) => a - b);
  if (!starts.length) return null;
  let best = 0;
  let bestGap = -1;
  for (let i = 0; i < starts.length; i += 1) {
    const prev = starts[(i - 1 + starts.length) % starts.length];
    const gap = (starts[i] - prev + 24) % 24 || 24;
    if (gap > bestGap) {
      bestGap = gap;
      best = i;
    }
  }
  return starts[best];
}

/**
 * A set's { start, end } in grid hours (may exceed 24), or null when it cannot be drawn.
 * `anchor`: the day's dayStartHour; a start before it belongs to that night's small hours.
 */
export function setSpan(row, { anchor = null } = {}) {
  let start = parseTimeToHour(row?.set_start_time);
  let end = parseTimeToHour(row?.set_end_time);
  if (start == null) return null;
  if (end == null) end = start + OPEN_SET_HOURS; // no listed end: an hour, so the set is still on the grid
  if (anchor != null && start < anchor) {
    start += 24;
    end += 24;
  }
  if (end < start) end += 24; // crosses midnight
  if (end <= start || end - start > MAX_SET_HOURS) return null;
  return { start, end };
}

/** setSpan for every row of one day, keyed by row. */
export function scheduleSpans(rows = []) {
  const anchor = dayStartHour(rows);
  const spans = new Map();
  rows.forEach((row) => {
    const span = setSpan(row, { anchor });
    if (span) spans.set(row, span);
  });
  return spans;
}

/** Sort key for running order: a set's start in grid hours (small hours after the evening), untimed last. */
export function runningOrderHour(row, anchor) {
  const start = parseTimeToHour(row?.set_start_time);
  if (start == null) return Infinity;
  return anchor != null && start < anchor ? start + 24 : start;
}

export function scheduleBounds(rows = [], spans = scheduleSpans(rows)) {
  let dayStart = Infinity;
  let dayEnd = -Infinity;
  rows.forEach((row) => {
    const span = spans.get(row);
    if (!span) return;
    dayStart = Math.min(dayStart, span.start);
    dayEnd = Math.max(dayEnd, span.end);
  });
  if (!Number.isFinite(dayStart) || !Number.isFinite(dayEnd)) {
    return { dayStart: 12, dayEnd: 24, gridHeight: 12 * PX_PER_HOUR };
  }
  if (dayEnd <= dayStart) dayEnd = dayStart + 1;
  return { dayStart, dayEnd, gridHeight: (dayEnd - dayStart) * PX_PER_HOUR };
}

export function groupByStage(rows = []) {
  const map = new Map();
  rows.forEach((row) => {
    const stage = row.stage || 'TBA';
    if (!map.has(stage)) map.set(stage, []);
    map.get(stage).push(row);
  });
  return [...map.entries()].map(([stage, stageRows]) => ({ stage, rows: stageRows }));
}

export function makeScheduleLayout(dayEnd) {
  return {
    toTop: (hour) => (dayEnd - hour) * PX_PER_HOUR,
    toHeight: (start, end) => (end - start) * PX_PER_HOUR,
  };
}

/** Hour labels for the time rail (inclusive ends, reversed top-to-bottom). */
export function hourLabels(dayStart, dayEnd) {
  const labels = [];
  const first = Math.floor(dayStart);
  const last = Math.ceil(dayEnd);
  for (let h = last; h >= first; h -= 1) {
    labels.push({ hour: h, label: formatHourLabel(h) });
  }
  return labels;
}

function formatHourLabel(hour) {
  const h = ((Math.floor(hour) % 24) + 24) % 24;
  const period = h >= 12 ? 'PM' : 'AM';
  const display = h % 12 === 0 ? 12 : h % 12;
  return `${display}${period}`;
}

/** Current decimal hour for the "now" line when viewing today's schedule. */
export function currentDecimalHour(now = new Date()) {
  return now.getHours() + now.getMinutes() / 60 + now.getSeconds() / 3600;
}

function localDateKey(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function isToday(dayStr) {
  if (!dayStr) return false;
  return dayStr === localDateKey(new Date());
}

/**
 * Where "now" sits on a day's grid, in grid hours, or null when now is not on it. After midnight the
 * previous day's grid still owns the small hours (24+).
 */
export function nowGridHour(dayStr, now = new Date()) {
  if (!dayStr) return null;
  const hour = currentDecimalHour(now);
  if (dayStr === localDateKey(now)) return hour;
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dayStr === localDateKey(yesterday)) return hour + 24;
  return null;
}
